import { useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';

/** Root-level custom property carrying this bar's measured height (#1750). */
export const ACTION_BAR_HEIGHT_VAR = '--action-bar-height';

/**
 * v4 mobile action bar (#1377 board): a zone pinned above the fixed bottom
 * nav carrying the screen's primary action (full-width button) and an
 * optional quiet hint line above it. Mobile only - desktop screens use the
 * per-section [+] buttons and inline forms instead. Callers render it
 * conditionally (xs viewports).
 *
 * @param {object} props
 * @param {React.ReactNode} [props.hint]  11px faint centered hint above the action
 * @param {React.ReactNode} props.children  the action button(s)
 * @param {object} [props.sx]  extra sx merged onto the bar
 */
const ActionBar = ({ hint, children, sx, ...rest }) => {
  const theme = useTheme();
  const { palette } = theme;
  const barRef = useRef(null);

  // #1750: the bottom-nav slide-up drawer is pinned to the same 56px offset as
  // this bar and cannot otherwise tell that a bar is in its way, so it used to
  // render underneath one. Publish the measured height on the document root;
  // Navigation reads it to seat the drawer above the bar and to cap the drawer
  // list against the space actually left. The height is not a constant - the
  // optional hint line and the button's own metrics both move it - so it is
  // measured rather than assumed.
  //
  // One document-level property is enough because at most one ActionBar is
  // mounted at a time: every caller renders it behind a single-screen card-view
  // condition. Removing the property on unmount is what makes bar-less pages
  // fall back to the 0px default and behave exactly as they did before.
  useEffect(() => {
    const node = barRef.current;
    if (!node) return undefined;

    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty(ACTION_BAR_HEIGHT_VAR, `${node.offsetHeight}px`);
    };
    publish();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish);
      observer.observe(node);
    }

    return () => {
      if (observer) observer.disconnect();
      root.style.removeProperty(ACTION_BAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        // Sits directly above the fixed bottom nav (56px tall).
        bottom: '56px',
        zIndex: 999,
        backgroundColor: palette.surface.page,
        padding: '8px 20px 10px',
        ...sx,
      }}
      {...rest}
    >
      {hint != null && (
        <Box
          sx={{
            fontSize: '11px',
            color: palette.text.faint,
            textAlign: 'center',
            paddingBottom: '8px',
          }}
        >
          {hint}
        </Box>
      )}
      {children}
    </Box>
  );
};

export default ActionBar;
